import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  Orbit, FlaskConical, Bot, GraduationCap, ArrowRight, Target, Award,
  BookOpen, Flame, CheckCircle2, PlayCircle, Sparkles, TrendingUp, Lightbulb
} from "lucide-react";

const QUICK = [
  { id: "projectile", title: "Projectile Motion", tag: "Kinematics" },
  { id: "pendulum", title: "Simple Pendulum", tag: "Oscillations" },
  { id: "orbit", title: "Gravity & Orbits", tag: "Gravitation" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lastActivity, setLastActivity] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    api.get("/analytics/me").then((r) => setStats(r.data)).catch(() => {});
    api.get("/student/assignments").then((r) => setAssignments(r.data || [])).catch(() => {});
    api.get("/student/notices").then((r) => setNotices(r.data || [])).catch(() => {});
    try {
      const saved = localStorage.getItem("pv_last_activity");
      if (saved) {
        setLastActivity(JSON.parse(saved));
      }
    } catch (_) {}
  }, []);

  const cards = [
    { to: "/app/universe", icon: Orbit, title: "Physics Universe", desc: "Explore interactive Mechanics simulations", color: "bg-blue-50 text-blue-600" },
    { to: "/app/lab", icon: FlaskConical, title: "Virtual Lab", desc: "Perform experiments & get a lab report", color: "bg-emerald-50 text-emerald-600" },
    { to: "/app/tutor", icon: Bot, title: "AI Tutor", desc: "Ask anything, derive step by step", color: "bg-amber-50 text-amber-600" },
    { to: "/app/practice", icon: GraduationCap, title: "JEE/NEET Practice", desc: "Adaptive problems with solutions", color: "bg-violet-50 text-violet-600" },
  ];

  // Daily goals computation
  const problemsSolved = stats?.total_attempts || 0;
  const labReportsDone = stats?.lab_reports || 0;
  const goalProblems = 3;
  const goalLabs = 1;
  const problemsDone = Math.min(goalProblems, problemsSolved);
  const labsDone = Math.min(goalLabs, labReportsDone);
  const dailyProgress = Math.round(((problemsDone / goalProblems) * 60) + ((labsDone / goalLabs) * 40));

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground font-mono-data">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1 className="font-display font-bold text-3xl lg:text-4xl text-slate-900 mt-1">
            Hi {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-muted-foreground mt-1">Ready to experiment? Here's where you left off.</p>
        </div>

        {/* Daily Streak Badge */}
        <div className="flex items-center gap-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 px-4 py-2 rounded-2xl shadow-sm">
          <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600">
            <Flame className="w-5 h-5 fill-amber-500 text-amber-600" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-display font-bold text-slate-900 text-sm">3 Day Streak</span>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            </div>
            <p className="text-[11px] text-muted-foreground font-mono-data">Daily Target: {dailyProgress}% done</p>
          </div>
        </div>
      </div>

      {/* stat strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Target} label="Accuracy" value={stats ? `${stats.accuracy}%` : "—"} sub={`${stats?.total_attempts || 0} attempts`} />
        <Stat icon={Award} label="Exam readiness" value={stats ? `${stats.exam_readiness}%` : "—"} sub="Mechanics" />
        <Stat icon={FlaskConical} label="Lab reports" value={stats?.lab_reports ?? "—"} sub="completed" />
        <Stat icon={BookOpen} label="Notes" value={stats?.notes ?? "—"} sub="saved" />
      </div>

      {/* Resume Last Activity Banner */}
      {lastActivity && (
        <div className="mt-8 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 p-0.5 shadow-sm">
          <div className="bg-white rounded-[14px] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <PlayCircle className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono-data uppercase tracking-wider text-primary font-bold">Resume where you left off</span>
                  <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono-data uppercase font-semibold">{lastActivity.type}</span>
                </div>
                <h3 className="font-display font-bold text-lg sm:text-xl text-slate-900 mt-0.5">{lastActivity.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Topic: {lastActivity.topic || "Mechanics"} · Pick up right where you paused</p>
              </div>
            </div>
            <Link to={lastActivity.to} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-blue-600 text-white font-medium text-sm transition-all shadow-sm shrink-0">
              Continue <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Daily Goals & AI Recommendation Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mt-8">
        {/* Daily Goal checklist */}
        <div className="lg:col-span-7 rounded-2xl border border-border bg-card p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              <h3 className="font-display font-semibold text-base text-slate-900">Today's Study Targets</h3>
            </div>
            <span className="text-xs font-mono-data text-muted-foreground">{dailyProgress}% Completed</span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 text-sm">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className={`w-4 h-4 ${problemsDone >= goalProblems ? "text-emerald-500 fill-emerald-100" : "text-slate-300"}`} />
                <span className={problemsDone >= goalProblems ? "line-through text-muted-foreground" : "text-slate-800"}>Solve {goalProblems} Practice Problems</span>
              </div>
              <span className="text-xs font-mono-data text-muted-foreground">{problemsDone}/{goalProblems}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 text-sm">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className={`w-4 h-4 ${labsDone >= goalLabs ? "text-emerald-500 fill-emerald-100" : "text-slate-300"}`} />
                <span className={labsDone >= goalLabs ? "line-through text-muted-foreground" : "text-slate-800"}>Perform 1 Virtual Lab Experiment</span>
              </div>
              <span className="text-xs font-mono-data text-muted-foreground">{labsDone}/{goalLabs}</span>
            </div>
          </div>
        </div>

        {/* AI Focus Recommendation */}
        <div className="lg:col-span-5 rounded-2xl border border-blue-200/70 bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/40 p-5 sm:p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-mono-data text-primary font-bold uppercase mb-2">
              <Sparkles className="w-3.5 h-3.5" /> AI Recommended Focus
            </div>
            <h4 className="font-display font-bold text-base text-slate-900">Projectile Motion & Kinematics</h4>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              High weightage in JEE Main & NEET (8–12 marks). Master angle of projection and air drag trade-offs.
            </p>
          </div>
          <div className="pt-4 flex items-center gap-2">
            <Link to="/app/sim/projectile" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-primary text-white hover:bg-blue-600 transition-colors inline-flex items-center gap-1">
              Launch Simulation <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/app/practice" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-border text-slate-700 hover:bg-slate-50 transition-colors">
              Solve PYQs
            </Link>
          </div>
        </div>
      </div>

      {/* Assignments & Notices Row */}
      {(assignments.length > 0 || notices.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {assignments.length > 0 && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-base text-slate-900 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" /> Active Assignments
                </h3>
                <span className="text-xs font-mono-data bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                  {assignments.length} assigned
                </span>
              </div>
              <div className="space-y-2.5">
                {assignments.slice(0, 3).map((a) => (
                  <div key={a.id} className="p-3 bg-white rounded-xl border border-blue-100 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-slate-900">{a.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Due: {a.due_date} · By {a.created_by}</p>
                    </div>
                    <Link
                      to={a.type === "lab" ? "/app/lab" : a.type === "practice" ? "/app/practice" : `/app/sim/${a.target_id || "projectile"}`}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary text-white hover:bg-blue-600 transition-colors"
                    >
                      Start
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
          {notices.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-base text-slate-900 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-600" /> Class Notices
                </h3>
                <span className="text-xs font-mono-data bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                  {notices.length} updates
                </span>
              </div>
              <div className="space-y-2.5">
                {notices.slice(0, 3).map((n) => (
                  <div key={n.id} className="p-3 bg-white rounded-xl border border-amber-100 text-sm">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-slate-900">{n.title}</p>
                      {n.priority === "urgent" && (
                        <span className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold uppercase">Urgent</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* modules */}
      <h2 className="font-display font-semibold text-xl text-slate-900 mt-10 mb-4">Jump back in</h2>
      <div className="grid sm:grid-cols-2 gap-5">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} data-testid={`dash-card-${c.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className="group rounded-2xl border border-border bg-card p-6 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.color}`}>
              <c.icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-lg text-slate-900">{c.title}</h3>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </div>
              <p className="text-sm text-muted-foreground mt-1">{c.desc}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* quick sims */}
      <h2 className="font-display font-semibold text-xl text-slate-900 mt-10 mb-4">Popular simulations</h2>
      <div className="grid sm:grid-cols-3 gap-5">
        {QUICK.map((q) => (
          <Link key={q.id} to={`/app/sim/${q.id}`} data-testid={`quick-sim-${q.id}`}
            className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className="h-28 blueprint flex items-center justify-center">
              <Orbit className="w-9 h-9 text-primary/60" />
            </div>
            <div className="p-4">
              <span className="text-[11px] font-mono-data text-muted-foreground">{q.tag}</span>
              <h3 className="font-display font-semibold text-slate-900">{q.title}</h3>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground mb-3">
        <Icon className="w-4 h-4" />
        <span className="text-xs">{label}</span>
      </div>
      <p className="font-display font-bold text-2xl text-slate-900">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
    </div>
  );
}
